import React, { useState, useEffect } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import {
  ProductInfo,
  ProductImages,
  FeaturesList,
  TechnicalDetails,
  FAQSection,
} from "../components/camperDetails";
import EnhancedBookingForm from "../components/camperDetails/EnhancedBookingForm";
import { Header, WhySunCatchers } from "../components/shared";
import LoadingSpinner from "../components/shared/LoadingSpinner";
import { CheckoutFooter } from "../components/checkout";
import { fetchVehicleDetails } from "../services/vehicleService";
import { useTranslation } from "../hooks/useTranslation";
import { MEDIA_URL } from '../config/constants';
import { SEO, generateProductSchema, generateFAQSchema, generateBreadcrumbSchema } from "../seo";
import "../styles/camperDetails/CamperDetailsBase.css";
import "../styles/camperDetails/WaveCatchersTheme.css";

const WaveCatchersDetails = ({ camperData }) => {
  const { id } = useParams();
  const location = useLocation();
  const { t, getLocalizedPath } = useTranslation('camperDetails');
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get vehicle ID from navigation state first, then route param, then default
  // This allows clicking different Wave Catchers to show the correct vehicle
  const vehicleId = location.state?.vehicleId || (id === 'wave-catchers' ? 2 : parseInt(id) || 2);

  useEffect(() => {
    let isCancelled = false;
    
    const loadVehicleData = async () => {
      try {
        setLoading(true);
        const data = await fetchVehicleDetails(vehicleId);
        
        // Only update state if the component is still mounted and request wasn't cancelled
        if (!isCancelled) {
          setVehicleData(data);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err.message);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadVehicleData();
    
    // Cleanup function to cancel the request if component unmounts
    return () => {
      isCancelled = true;
    };
  }, [vehicleId]);

  // For dynamic vehicle IDs, check vehicle_design from loaded data
  if (vehicleData && vehicleData.VehicleDesign?.String === 'Sun Catcher') {
    return <Navigate to={getLocalizedPath("/campervan/sun-catchers")} replace />;
  }

  if (loading) {
    return <LoadingSpinner text={t('loading')} />;
  }

  if (error || !vehicleData) {
    return <div>{t('error')}: {error}</div>;
  }

  // Static images for Wave Catcher - simpler than database
  const staticImages = [
    `${MEDIA_URL}/images/WAVE CATCHER/WaveCatcher0001.webp`,
    `${MEDIA_URL}/images/WAVE CATCHER/WaveCatcher0002.webp`,
    `${MEDIA_URL}/images/WAVE CATCHER/WaveCatcher0003.webp`,
    `${MEDIA_URL}/images/WAVE CATCHER/WaveCatcher0004.webp`,
    `${MEDIA_URL}/images/WAVE CATCHER/WaveCatcher0005.webp`,
    `${MEDIA_URL}/images/WAVE CATCHER/WaveCatcher0006.webp`,
    `${MEDIA_URL}/images/WAVE CATCHER/WaveCatcher0007.webp`,
    `${MEDIA_URL}/images/WAVE CATCHER/WaveCatcher0008.webp`,
    `${MEDIA_URL}/images/WAVE CATCHER/WaveCatcher0009.webp`,
  ];

  const mainImage = staticImages[0];
  const thumbnailImages = staticImages.slice(1, 4); // Show 3 thumbnails
  const allImages = staticImages; // All images for gallery modal

  // Transform API data to component format
  const camperDataFormatted = {
    id: vehicleData.ID.toString(),
    name: vehicleData.Name?.String || "Wave Catchers",
    description: vehicleData.Description?.String || "Meet the Wave Catcher, our nautical-inspired camper designed for ocean lovers and coastal adventures. Perfect for surfers and beachgoers seeking morning waves, sunset sessions, and the freedom to explore Portugal's stunning coastline.",
    price: `$${vehicleData.DailyRate?.String || "86.48"}`,
    priceDescription: "Per Night",
    mainImage: mainImage,
    thumbnailImages: thumbnailImages,
    allImages: allImages, // All images for modal
    additionalImagesCount: 0,
    features: vehicleData.Features || {
      bathroom: [],
      bedroom: [],
      outdoor: [],
      kitchen: [],
      themeSpecs: [],
    },
    technicalDetails: [
      {
        icon: "van",
        title: t("technicalDetails.model"),
        description: "Fiat Ducato",
      },
      {
        icon: "engine",
        title: t("technicalDetails.power"),
        description: "115HP, 2.2l Engine",
      },
      {
        icon: "dimensions",
        title: t("technicalDetails.lengthHeight"),
        description: "599cm x 252cm",
      },
      {
        icon: "assistance",
        title: t("technicalDetails.driverAssistance"),
        description: "Front and rear parking sensors",
      },
    ],
    // Show the full list, not just the first few — the surf/sports questions
    // and the cancellation policy live further down it. Surf comes first here
    // because that is what this van is for.
    faqs: t('waveCatcherFaqs', { returnObjects: true }).map((faq, index) => ({
      ...faq,
      isOpen: index === 0
    })),
  };

  const camper = camperDataFormatted;

  // SEO Schemas
  const productSchema = generateProductSchema({
    name: camper.name,
    description: camper.description,
    image: camper.mainImage,
    dailyRate: vehicleData.DailyRate?.String || '60',
    slug: 'campervan/wave-catchers',
    rating: 4.8,
    reviewCount: 12,
  });

  const faqSchema = generateFAQSchema(camper.faqs);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', path: '' },
    { name: 'Catalog', path: '/catalog' },
    { name: camper.name, path: '/campervan/wave-catchers' },
  ]);

  return (
    <div className="wave-catchers-page">
      <SEO
        title={`${camper.name} | Surf Campervan for 5 | SunCatchers`}
        description={camper.description}
        image={camper.mainImage}
        schema={[productSchema, faqSchema, breadcrumbSchema].filter(Boolean)}
      />
      <h1 className="sr-only">{camper.name} - Surf Campervan Rental Portugal</h1>
      <div className="main-container">
        <Header />
        <div className="content-container">
          <div className="left-column">
            <div className="left-column-content">
              <ProductInfo
                name={camper.name}
                description={camper.description}
              />
              <ProductImages
                mainImage={camper.mainImage}
                thumbnailImages={camper.thumbnailImages}
                allImages={camper.allImages}
                additionalCount={camper.additionalImagesCount}
                vehicleName="Wave Catcher"
              />
            </div>
            <WhySunCatchers />
            <FeaturesList features={camper.features} />
            <TechnicalDetails details={camper.technicalDetails} />
            <FAQSection faqs={camper.faqs} />
          </div>
          <div className="booking-container">
            <EnhancedBookingForm
              price={camper.price}
              priceDescription={camper.priceDescription}
              vehicleId={vehicleId}
              vanTitle={camper.name}
              vanImage={camper.mainImage}
            />
          </div>
        </div>
      </div>
      <CheckoutFooter />
    </div>
  );
};

export default WaveCatchersDetails;
